<template>
  <UiComponentWrapper class="range">
    <UiComponentWrapper
      ref="$slider"
      class="slider"
    >
      <UiComponentWrapper class="background" />
      <UiComponentWrapper
        class="progress"
        :style="{ width: `${progress}%` }"
      />
      <UiComponentWrapper
        ref="$thumb"
        class="thumb"
        :style="{ left: `${progress}%` }"
      >
        <UiComponentWrapper class="inner">
          <UiComponentWrapper
            v-for="n in 3"
            :key="`line-${n}`"
            class="line"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentWrapper>
    <UiComponentWrapper
      class="input"
      :class="{ editable }"
      tabindex="0"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
    >
      <UiComponentWrapper class="value">
        {{ currentValue }}
      </UiComponentWrapper>
      <UiComponentInput
        ref="$input"
        type="number"
        @input.change="debouncedOnChange"
      />
    </UiComponentWrapper>
  </UiComponentWrapper>
</template>

<script setup>
import { gsap } from 'gsap'

// Emitter
const emit = defineEmits([
  'change',
])

// Props
const props = defineProps({
  steps: {
    default() {
      return []
    },
    required: false,
    type: Array,
  },
})

// Refs
const $input = ref(null)
const $slider = ref(null)
const $thumb = ref(null)
const editable = ref(false)
const index = ref(0)
const isDragging = ref(false)
const progress = ref(0)

// Constants
const useDebounceFn = (fn, delay) => {
  let timeout

  return (...args) => {
    if (timeout) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(() => {
      fn(...args)
    }, delay)
  }
}

const debouncedOnChange = useDebounceFn(() => {
  const value = Number.parseInt($input.value.getValue())

  if (isNaN(value) || value < minimum.value) {
    progress.value = 0
    $input.value.setValue(minimum.value)
    emit('change', minimum.value)
  }
  else if (value >= maximum.value) {
    progress.value = 100
    emit('change', value)
  }
  else {
    const reversedIndex = [...props.steps]
      .reverse()
      .findIndex(number => value >= number)
    const index = props.steps.length - 1 - reversedIndex

    progress.value
      = (index * aStepInPercentage.value)
        + (((value - props.steps[index]) / (props.steps[index + 1] - props.steps[index])) * aStepInPercentage.value)

    emit('change', value)
  }
}, 1000)

// Computed Properties
const aStepInPercentage = computed(() => {
  return 100 / (props.steps.length - 1)
})

const currentStepIndex = computed(() => {
  return Math.floor(progress.value / aStepInPercentage.value)
})

const currentValue = computed(() => {
  if (!$input.value) {
    return minimum.value
  }

  return $input.value?.getValue() >= maximum.value
    ? `${$input.value?.getValue()}+`
    : $input.value?.getValue()
})

const maximum = computed(() => {
  return props.steps[props.steps.length - 1]
})

const minimum = computed(() => {
  return props.steps[0]
})

const sliderSteps = computed(() => {
  return props.steps.map((_, index) => {
    return aStepInPercentage.value * index
  })
})

// Methods
function onDragStart() {
  const dragEnd = 'ontouchend' in document ? 'touchend' : 'mouseup'
  document.addEventListener(dragEnd, onDragEnd)
  isDragging.value = true
}

function onDrag() {
  if (!isDragging.value) {
    return
  }

  const { clientX } = event.type === 'touchmove' ? event.touches[0] : event
  const { x, width } = $slider.value.$el.getBoundingClientRect()
  const offset = x - clientX

  if (offset >= 0) {
    progress.value = 0
  }
  else if (Math.abs(offset) >= width) {
    progress.value = 100
  }
  else {
    const offsetInPercentage = (Math.abs(offset) / width) * 100
    progress.value = offsetInPercentage
  }

  nextTick(() => {
    const relativeValue = Math.round(
      props.steps[currentStepIndex.value]
      + (
        (props.steps[currentStepIndex.value + 1] - props.steps[currentStepIndex.value])
        * ((progress.value % aStepInPercentage.value) / aStepInPercentage.value)
      ),
    )

    const containedValue = Number.isNaN(relativeValue) ? maximum.value : relativeValue
    const displayedValue = containedValue >= 100 ? 10 * Math.floor(containedValue / 10) : containedValue

    $input.value.setValue(displayedValue)

    emit('change', displayedValue)
  })
}

function onDragEnd() {
  const dragEnd = 'ontouchend' in document ? 'touchend' : 'mouseup'
  document.removeEventListener(dragEnd, onDragEnd)
  isDragging.value = false
}

function onFocusIn() {
  editable.value = true
  nextTick(() => {
    $input.value.$el.focus()
  })
}

function onFocusOut() {
  editable.value = false
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  const dragStart = 'ontouchstart' in document ? 'touchstart' : 'mousedown'
  const drag = 'ontouchmove' in document ? 'touchmove' : 'mousemove'
  $thumb.value?.$el?.removeEventListener(dragStart, onDragStart)
  document.removeEventListener(drag, onDrag, { passive: true })
})

onMounted(() => {
  const dragStart = 'ontouchstart' in document ? 'touchstart' : 'mousedown'
  const drag = 'ontouchmove' in document ? 'touchmove' : 'mousemove'
  $input.value.setValue(minimum.value)
  $thumb.value.$el.addEventListener(dragStart, onDragStart)
  document.addEventListener(drag, onDrag, { passive: true })
})
</script>

<style lang="postcss" scoped>
.range {
  .slider {
    @apply relative items-center h-8;

    .background {
      @apply absolute h-[3px] bg-grey-blue-200 z-0 rounded-full;
    }

    .progress {
      @apply absolute h-[3px] w-0 bg-livestorm-blue-600 z-0 rounded-full;
    }

    .thumb {
      @apply absolute top-1/2 self-start w-auto cursor-pointer transform -translate-x-1/2 -translate-y-1/2 shadow-light1;

      .inner {
        @apply items-center justify-center h-[15px] w-[18px] border border-grey-blue-300 bg-white rounded-[4px];

        .line {
          @apply h-[5px] w-px mx-px bg-grey-blue-300;
        }
      }
    }
  }

  .input {
    @apply relative flex-shrink-0 items-center justify-center h-8 w-14 ml-6 px-2 py-[7px] bg-white border border-grey-blue-300 text-winter-green-900 text-xs text-center leading-4 rounded-xs;

    &.editable {
      @apply border-livestorm-blue-600;

      input {
        @apply flex;
      }

      .value {
        @apply hidden;
      }
    }

    input {
      @apply hidden bg-transparent border-none px-0 text-winter-green-900 text-xs text-center leading-4 rounded-none;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        @apply m-0 appearance-none;
      }
    }

    .value {
      @apply w-auto text-center;
    }
  }
}
</style>
